import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Box, Typography, Link, Divider, useTheme, Paper } from '@mui/material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ImageLoader from './ImageLoader'; // Create this component or use a placeholder

interface MarkdownRendererProps {
  context: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ context }) => {
  const theme = useTheme();
  
  // If no content, show placeholder
  if (!context || context.trim() === '') {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">No content to display</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        '& img': {
          maxWidth: '100%',
          height: 'auto',
          borderRadius: 1,
          my: 2,
        },
        fontSize: '1rem',
        lineHeight: 1.6,
      }}
    >
      <ReactMarkdown
        children={context}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          h1: ({ node, ...props }) => (
            <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 3, fontWeight: 'bold', color: theme.palette.primary.main }} {...props} />
          ),
          h2: ({ node, ...props }) => (
            <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3, fontWeight: 'bold', color: theme.palette.primary.main }} {...props} />
          ),
          h3: ({ node, ...props }) => (
            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }} {...props} />
          ),
          h4: ({ node, ...props }) => (
            <Typography variant="subtitle1" component="h4" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }} {...props} />
          ),
          p: ({ node, ...props }) => (
            <Typography variant="body1" gutterBottom {...props} />
          ),
          a: ({ node, ...props }) => (
            <Link color="primary" {...props} />
          ),
          img: ({ src, alt, ...props }) => (
            <ImageLoader src={src || ''} alt={alt || ''} {...props} />
          ),
          blockquote: ({ node, ...props }) => (
            <Paper 
              elevation={0}
              sx={{
                pl: 2,
                py: 0.5,
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                bgcolor: 'background.default',
                my: 2,
              }}
              {...props}
            />
          ),
          hr: () => <Divider sx={{ my: 2 }} />,
          code: ({ node, inline, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
              <SyntaxHighlighter
                style={atomDark}
                language={match[1]}
                PreTag="div"
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <Box
                component="code"
                sx={{
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                  px: 0.8,
                  py: 0.2,
                  borderRadius: 1,
                  fontFamily: 'Monospace',
                  fontSize: '0.875em',
                }}
                {...props}
              >
                {children}
              </Box>
            );
          },
          ul: ({ node, ...props }) => (
            <Box component="ul" sx={{ pl: 2, mb: 2 }} {...props} />
          ),
          ol: ({ node, ...props }) => (
            <Box component="ol" sx={{ pl: 2, mb: 2 }} {...props} />
          ),
          li: ({ node, ...props }) => (
            <Box component="li" sx={{ mb: 0.5 }} {...props} />
          ),
          table: ({ node, ...props }) => (
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                mb: 2,
                '& th, & td': {
                  border: `1px solid ${theme.palette.divider}`,
                  p: 1,
                },
              }}
              {...props}
            />
          ),
        }}
      />
    </Box>
  );
};

export default MarkdownRenderer;
